import { useEffect, useRef, useState } from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';

/**
 * Tracks whether a react-hook-form form has unsaved changes vs. its initial state.
 *
 * Works around three RHF v7 / Vivli-form quirks in our hand-rolled wrapper-state setup:
 *   1. Reading `formApi.formState.isDirty` inside a `formApi.watch()` callback returns
 *      the previous-cycle value (proxy lag). We snapshot the `values` argument instead —
 *      that IS current.
 *   2. JSON.stringify treats "field never set" (omitted from values) and "field touched
 *      then cleared to '' / null" as different strings, so a revert-to-empty would not
 *      undirty the form. We normalize empty values (undefined / null / '') out of both
 *      sides of the comparison so they register as equivalent.
 *   3. Date fields loaded from the API are typically ISO strings with a UTC time component
 *      (e.g. "2026-05-22T00:00:00.000Z"), while calendar-picked dates produce Date objects
 *      whose JSON serialization carries the local-midnight TZ offset (e.g.
 *      "2026-05-22T04:00:00.000Z"). Same user-facing day, different strings — would leave
 *      a "pick the original date back" revert looking dirty. We reduce date-like values to
 *      YYYY-MM-DD before comparison so the time/TZ noise disappears.
 *
 * Returns `isFormDirty` and `resetBaseline()` — call `resetBaseline()` after a successful
 * save (and after `formApi.reset(newValues)`) so the new persisted state becomes the
 * baseline for the next user edit.
 *
 * TODO: retire when the wrapper refactor moves to formApi.formState as source of truth.
 */
export function useFormDirtyTracking<T extends FieldValues>(
    formApi: UseFormReturn<T> | undefined
): { isFormDirty: boolean; resetBaseline: () => void } {
    const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
    const baselineValuesRef = useRef<string>('');

    useEffect(() => {
        if (!formApi) return;
        baselineValuesRef.current = JSON.stringify(normalizeEmpties(formApi.getValues()));
        const subscription = formApi.watch((values) => {
            setIsFormDirty(JSON.stringify(normalizeEmpties(values)) !== baselineValuesRef.current);
        });
        return () => subscription.unsubscribe();
    }, [formApi]);

    const resetBaseline = () => {
        if (!formApi) return;
        baselineValuesRef.current = JSON.stringify(normalizeEmpties(formApi.getValues()));
        setIsFormDirty(false);
    };

    return { isFormDirty, resetBaseline };
}

function normalizeEmpties(vals: unknown): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries((vals as Record<string, unknown>) || {})) {
        if (v === undefined || v === null || v === '') continue;
        // Date-like → reduce to YYYY-MM-DD so loaded ISO strings and calendar-picked
        // Date objects compare equal despite TZ / time-of-day differences.
        if (v instanceof Date) {
            out[k] = `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, '0')}-${String(v.getDate()).padStart(2, '0')}`;
        } else if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
            out[k] = v.substring(0, 10);
        } else {
            out[k] = v;
        }
    }
    return out;
}
